import React from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import debounce from "lodash/debounce";
import { contentHash, fixArabicFont } from "../../helpers";
import { savePage } from "../../reducers/pages/actions";
import { getVisibilityProps, EVT_WIN_BEFOREUNLOAD } from "../../lib";
import { getIds, pageOrDocChanged, compareHashWithContents } from "./helpers";

const Container = styled.div<{ enableDarkTheme: boolean }>`
    position: fixed;
    top: 35px;
    width: 100%;
    font-size: 24px;
    font-weight: 700 !important;
    font-family: urdu, QuranFont, sans-serif;
    text-align: center;
    color: ${(p) => (p.enableDarkTheme ? "#fff" : "#000")};
    outline: none;
`;

const mapDispatch = { savePage };
type DispatchProps = typeof mapDispatch;
type OwnProps = {
    readOnly: boolean;
    enableDarkTheme: boolean;

    doc_id: number;
    page_id: number;
    page: any;
};
type Props = DispatchProps & OwnProps;
type State = {
    counter: number;
    focused: boolean;
};
class Name extends React.Component<Props, State> {
    private name: string = "";
    private save: ReturnType<typeof debounce>;

    constructor(props: Props) {
        super(props);

        this.state = {
            counter: 0,
            focused: false,
        };

        this.save = debounce(this._save, 5 * 1000);
        this.setupEvents();
    }

    setupEvents() {
        window.addEventListener(EVT_WIN_BEFOREUNLOAD, () => this.save.flush());

        const { visibilityChange } = getVisibilityProps();
        window.addEventListener(visibilityChange, () => {
            if (document.hidden) {
                this.save.flush();
            }
        });
    }

    componentDidMount() {
        this.setName(this.props);
    }

    componentWillUnmount() {
        this.save.flush();
    }

    componentWillReceiveProps(nextProps: Props, nextState: State) {
        if (pageOrDocChanged(this.props, nextProps)) {
            this.save.flush();
            this.setName(nextProps);
        }

        if (
            this.name === "" &&
            nextProps.page &&
            this.name !== nextProps.page.get("name")
        ) {
            this.setName(nextProps);
        }
    }

    shouldComponentUpdate(nextProps: Props, nextState: State) {
        return (
            this.state.focused !== nextState.focused ||
            this.state.counter !== nextState.counter ||
            this.props.enableDarkTheme !== nextProps.enableDarkTheme
        );
    }

    private lastSaveHash: string = "";
    _save = () => {
        const contents = this.name;
        if (!compareHashWithContents(this.lastSaveHash, contents)) {
            this.lastSaveHash = contentHash(contents);

            const { doc_id, page_id } = getIds(this.props);
            this.props.savePage({ doc_id, page_id, data: { name: contents } });
        }
    };

    onFocus = () => this.setState({ focused: true });
    onBlur = () => this.setState({ focused: false });

    onChange = (contents: string) => {
        this.name = contents;
        this.save();
    };

    onKeyUp = (evt: React.KeyboardEvent<HTMLDivElement>) =>
        this.onChange((evt.target as HTMLDivElement).innerText);

    getName = () => {
        if (!this.state.focused) {
            return fixArabicFont(this.name);
        }

        return this.name;
    };

    setName = ({ page }: Props) => {
        this.name = page ? page.get("name") : "";
        this.setState({ counter: this.state.counter + 1 });
    };

    markup() {
        return {
            __html: this.getName(),
        };
    }

    render() {
        const { readOnly } = this.props;
        return (
            <Container
                contentEditable={!readOnly}
                onKeyUp={this.onKeyUp}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                enableDarkTheme={this.props.enableDarkTheme}
                dangerouslySetInnerHTML={this.markup()}
            />
        );
    }
}

export default connect(null, mapDispatch)(Name);
